import React, { useState, useEffect, useCallback } from "react";
import {
  Plus, Wand2, Server, ShieldCheck, Users, ArrowLeft, Pencil, Trash2,
  Bot, BookMarked, Check, RefreshCw, X, ChevronDown, ChevronRight,
  Lock, Wrench, MessageSquare, Database, Layers, Search, Link2, GraduationCap,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { useToast } from "../../../../hooks/use-toast";
import { Button } from "../../../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../../ui/dialog";
import GatewayStatCard from "../components/GatewayStatCard";
import GatewayChatPanel from "../components/GatewayChatPanel";
import { GatewayProfilesApi, GatewayAgentsApi } from "../Api/gatewayApi";
import { formatTimestamp, apiError, capabilityTypeColor } from "../utils/gatewayUtils";
import { ChatMessage } from "../interfaces/gatewayInterfaces";
import "../../ManagementDialog.css";
import "../AgenticGateway.css";


type PageView = "list" | "form" | "detail";
type ServerMode = "all" | "readonly" | "none" | "custom";

function priorityFilter<T>(items: T[], query: string, getName: (item: T) => string): T[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase().trim();
  return items.filter((item) => getName(item).toLowerCase().startsWith(q));
}

interface CapRule {
  serverConfigName: string;
  capabilityType: string;
  mode: string;
  capabilityNames?: string[] | string | null;
}

interface Profile {
  id: string;
  name: string;
  description?: string;
  isTemplate?: boolean;
  rules: CapRule[];
  assignedAgents?: { agentId: string; agentName: string }[];
  assignedAgentCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface AvailableServer {
  serverConfigName?: string;
  name?: string;
  active?: boolean;
  tools?: any[];
  prompts?: any[];
  resources?: any[];
  skills?: any[];
}


function isReadOnlyTool(name: string): boolean {
  if (!name) return false;
  return /\b(list|get|search|read|describe|view|fetch|find|query|show|count)\b/.test(
    name.toLowerCase()
  );
}

function capabilityName(cap: any): string {
  return typeof cap === "string" ? cap : cap?.name || cap?.originalName || "";
}

function ruleModeLabel(mode: string): string {
  switch (mode) {
    case "INCLUDE_ALL": return "Include All";
    case "INCLUDE_ONLY": return "Include Only";
    case "EXCLUDE": return "Exclude";
    default: return mode;
  }
}

function capTypeIcon(type: string): React.ReactNode {
  switch (type) {
    case "TOOL": return <Wrench className="w-3 h-3" />;
    case "PROMPT": return <MessageSquare className="w-3 h-3" />;
    case "RESOURCE": return <Database className="w-3 h-3" />;
    case "SKILL": return <GraduationCap className="w-3 h-3" />;
    default: return <Layers className="w-3 h-3" />;
  }
}


function ProfileCard({
  profile,
  onClick,
  onEdit,
  onDelete,
}: {
  profile: Profile;
  onClick: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const rules = profile.rules || [];
  const serverCount = new Set(rules.map((r) => r.serverConfigName)).size;
  const assignedCount = profile.assignedAgentCount || 0;

  const ruleBadges = rules.slice(0, 4).map((r, i) => {
    const modeLabel =
      r.mode === "INCLUDE_ALL" ? "All" : r.mode === "INCLUDE_ONLY" ? "Only" : "Exclude";
    const modeClass =
      r.mode === "INCLUDE_ALL"
        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
        : r.mode === "INCLUDE_ONLY"
        ? "bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20"
        : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20";
    return (
      <span
        key={i}
        className={cn(
          "inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium border",
          modeClass
        )}
      >
        {capTypeIcon(r.capabilityType)}
        {r.serverConfigName} ({r.capabilityType || "ALL"}): {modeLabel}
      </span>
    );
  });
  const overflow = rules.length > 4 ? rules.length - 4 : 0;

  return (
    <div
      className="ag-entity ag-clickable"
      onClick={onClick}
    >
      <div className="ag-entity-head">
        <div className="ag-entity-identity">
          <span className="ag-entity-avatar" data-tone={profile.isTemplate ? "amber" : undefined} aria-hidden="true">
            {profile.isTemplate ? <BookMarked aria-label="Template" /> : <ShieldCheck />}
          </span>
          <div className="min-w-0">
            <h3 className="ag-entity-name" title={profile.name}>{profile.name}</h3>
            <p className="ag-hint line-clamp-1">{profile.description || "No description"}</p>
          </div>
        </div>
        <div
          className="ag-entity-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="ag-icon-button"
            onClick={onEdit}
            title="Edit"
          >
            <Pencil />
          </button>
          <button
            type="button"
            className="ag-icon-button"
            data-tone="danger"
            onClick={onDelete}
            title="Delete"
          >
            <Trash2 />
          </button>
        </div>
      </div>

      <div className="ag-entity-body">
        {ruleBadges.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {ruleBadges}
            {overflow > 0 && (
              <span className="ag-chip" data-tone="muted">+{overflow} more</span>
            )}
          </div>
        )}

        <div className="ag-server-metrics ag-metrics-3">
          <div className="ag-metric">
            <span className="ag-metric-label">Servers</span>
            <span className="ag-metric-value text-lg">{String(serverCount).padStart(2, "0")}</span>
          </div>
          <div className="ag-metric">
            <span className="ag-metric-label">Rules</span>
            <span className="ag-metric-value text-lg">{String(rules.length).padStart(2, "0")}</span>
          </div>
          <div className="ag-metric">
            <span className="ag-metric-label">Agents</span>
            <span className="ag-metric-value text-lg">{String(assignedCount).padStart(2, "0")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}


interface ServerCardState {
  mode: ServerMode;
  checkedTools: Set<string>;
  checkedPrompts: Set<string>;
  checkedResources: Set<string>;
  checkedSkills: Set<string>;
}

function ServerAccessCard({
  server,
  state,
  onChange,
  isOpen,
  onToggle,
}: {
  server: AvailableServer;
  state: ServerCardState;
  onChange: (next: ServerCardState) => void;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const serverName = server.serverConfigName || server.name || "";
  const tools = (server.tools || []).map(capabilityName);
  const prompts = (server.prompts || []).map(capabilityName);
  const resources = (server.resources || []).map(capabilityName);
  const skills = (server.skills || []).map(capabilityName);
  const totalCaps = tools.length + prompts.length + resources.length + skills.length;
  const isActive = server.active !== false;

  const [search, setSearch] = useState("");

  function setMode(mode: ServerMode) {
    const next: ServerCardState = { ...state, mode };
    if (mode === "all") {
      next.checkedTools = new Set(tools);
      next.checkedPrompts = new Set(prompts);
      next.checkedResources = new Set(resources);
      next.checkedSkills = new Set(skills);
    } else if (mode === "none") {
      next.checkedTools = new Set();
      next.checkedPrompts = new Set();
      next.checkedResources = new Set();
      next.checkedSkills = new Set();
    } else if (mode === "readonly") {
      next.checkedTools = new Set(tools.filter(isReadOnlyTool));
      next.checkedPrompts = new Set(prompts);
      next.checkedResources = new Set(resources);
      next.checkedSkills = new Set(skills);
    }
    onChange(next);
  }

  function toggleCap(
    type: "tools" | "prompts" | "resources" | "skills",
    name: string,
    checked: boolean
  ) {
    const key =
      type === "tools"
        ? "checkedTools"
        : type === "prompts"
        ? "checkedPrompts"
        : type === "resources"
        ? "checkedResources"
        : "checkedSkills";
    const next = new Set(state[key]);
    if (checked) next.add(name);
    else next.delete(name);
    onChange({ ...state, [key]: next });
  }

  function toggleAllType(type: "tools" | "prompts" | "resources" | "skills", all: string[]) {
    const key =
      type === "tools" ? "checkedTools"
        : type === "prompts" ? "checkedPrompts"
        : type === "resources" ? "checkedResources"
        : "checkedSkills";
    const allChecked = all.every((n) => state[key].has(n));
    const next = allChecked ? new Set<string>() : new Set(all);
    onChange({ ...state, [key]: next });
  }

  const modeButtons: { id: ServerMode; label: string }[] = [
    { id: "all", label: "All" },
    { id: "readonly", label: "Read-Only" },
    { id: "none", label: "None" },
    { id: "custom", label: "Custom" },
  ];

  const selectedCount = state.checkedTools.size + state.checkedPrompts.size + state.checkedResources.size + state.checkedSkills.size;
  function renderCapabilityGrid(
    label: string,
    type: "tools" | "prompts" | "resources" | "skills",
    items: string[],
    checkedSet: Set<string>
  ) {
    if (items.length === 0) return null;
    const filtered = search ? priorityFilter(items, search, (n) => n) : items;
    if (filtered.length === 0) return null;
    const allChecked = filtered.every((n) => checkedSet.has(n));

    return (
      <div className="mt-5">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {label} ({filtered.length})
          </span>
          <button
            type="button"
            onClick={() => toggleAllType(type, filtered)}
            className={cn(
              "text-[10px] px-2 py-0.5 rounded border transition-colors",
              allChecked
                ? "bg-primary/15 text-primary border-primary/30"
                : "ag-surface text-muted-foreground border-border hover:text-foreground"
            )}
          >
            {allChecked ? "Deselect All" : "Select All"}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
          {filtered.map((name) => {
            const checked = checkedSet.has(name);
            const accentColor = type === "tools"
              ? (checked ? "border-l-primary" : "border-l-border/60")
              : type === "prompts"
                ? (checked ? "border-l-violet-400" : "border-l-border/60")
                : type === "resources"
                  ? (checked ? "border-l-amber-400" : "border-l-border/60")
                  : (checked ? "border-l-fuchsia-400" : "border-l-border/60");
            return (
              <button
                key={name}
                type="button"
                onClick={() => toggleCap(type, name, !checked)}
                className={cn(
                  "group flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-l-[3px] border border-border text-xs font-mono transition-all duration-150 text-left min-w-0",
                  accentColor,
                  checked
                    ? "bg-primary/10 text-primary"
                    : "bg-card text-muted-foreground hover:bg-primary/[0.025] hover:text-foreground"
                )}
              >
                <span
                  className={cn(
                    "shrink-0 w-[18px] h-[18px] rounded-[5px] flex items-center justify-center border-[1.5px] transition-all duration-200",
                    checked
                      ? "bg-primary/15 border-primary"
                      : "bg-transparent border-muted-foreground/30 group-hover:border-muted-foreground/50"
                  )}
                >
                  <svg
                    viewBox="0 0 12 12"
                    fill="none"
                    className={cn(
                      "w-[10px] h-[10px] transition-all duration-200",
                      checked ? "opacity-100 scale-100" : "opacity-0 scale-50"
                    )}
                  >
                    <path d="M2.5 6L5 8.5L9.5 3.5" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="truncate">{name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="ag-card overflow-hidden portal-card-hover">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3"
      >
        <Server className="w-5 h-5 text-primary shrink-0" />
        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-foreground text-sm">{serverName}</span>
            {isActive && (
              <span className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
                ACTIVE
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {tools.length} tools, {prompts.length} prompts, {resources.length} resources{skills.length > 0 ? `, ${skills.length} skills` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {state.mode !== "none" && (
            <span className="text-[11px] text-primary font-medium">
              {state.mode === "all" ? "All" : state.mode === "readonly" ? "Read-Only" : `${selectedCount}/${totalCaps}`}
            </span>
          )}
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-border px-5 py-4 space-y-4">
          <div className="flex items-center gap-2">
            {modeButtons.map((btn) => (
              <button
                key={btn.id}
                type="button"
                className={cn(
                  "px-4 py-2 rounded-lg text-xs font-medium transition-all",
                  state.mode === btn.id
                    ? "bg-primary/15 text-primary border border-primary/30"
                    : "text-muted-foreground hover:text-foreground hover:bg-primary/[0.025]"
                )}
                onClick={() => setMode(btn.id)}
              >
                {btn.label}
              </button>
            ))}
          </div>

          {state.mode === "custom" && totalCaps > 0 && (
            <>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search capabilities..."
                  className="w-full max-w-sm pl-8 pr-3 py-1.5 rounded-lg border border-border bg-card text-xs text-foreground placeholder-muted-foreground outline-none focus:border-primary/50"
                />
              </div>

              {renderCapabilityGrid("Tools", "tools", tools, state.checkedTools)}
              {prompts.length > 0 && <div className="border-t border-border mt-4" />}
              {renderCapabilityGrid("Prompts", "prompts", prompts, state.checkedPrompts)}
              {resources.length > 0 && <div className="border-t border-border mt-4" />}
              {renderCapabilityGrid("Resources", "resources", resources, state.checkedResources)}
              {skills.length > 0 && <div className="border-t border-border mt-4" />}
              {renderCapabilityGrid("Skills", "skills", skills, state.checkedSkills)}
            </>
          )}
        </div>
      )}
    </div>
  );
}


function buildServerCardStates(
  servers: AvailableServer[],
  profile: Profile | null
): Map<string, ServerCardState> {
  const map = new Map<string, ServerCardState>();

  const existingRules: Record<string, CapRule[]> = {};
  if (profile?.rules) {
    profile.rules.forEach((r) => {
      if (!existingRules[r.serverConfigName]) existingRules[r.serverConfigName] = [];
      existingRules[r.serverConfigName].push(r);
    });
  }

  servers.forEach((server) => {
    const serverName = server.serverConfigName || server.name || "";
    const tools = (server.tools || []).map(capabilityName);
    const prompts = (server.prompts || []).map(capabilityName);
    const resources = (server.resources || []).map(capabilityName);
    const skills = (server.skills || []).map(capabilityName);
    const serverRules = existingRules[serverName] || [];

    let mode: ServerMode = "none";
    let checkedTools = new Set<string>();
    let checkedPrompts = new Set<string>();
    let checkedResources = new Set<string>();
    let checkedSkills = new Set<string>();

    if (serverRules.length === 1 && serverRules[0].capabilityType === "ALL" && serverRules[0].mode === "INCLUDE_ALL") {
      mode = "all";
      checkedTools = new Set(tools);
      checkedPrompts = new Set(prompts);
      checkedResources = new Set(resources);
      checkedSkills = new Set(skills);
    } else if (serverRules.length > 0) {
      const onlyRule = serverRules.find((r) => r.mode === "INCLUDE_ONLY");
      if (onlyRule && onlyRule.capabilityNames) {
        const names = Array.isArray(onlyRule.capabilityNames)
          ? onlyRule.capabilityNames
          : onlyRule.capabilityNames.split(",").map((n) => n.trim());
        const readOnlyNames = tools.filter(isReadOnlyTool);
        if (names.length === readOnlyNames.length && names.every((n) => readOnlyNames.includes(n))) {
          mode = "readonly";
          checkedTools = new Set(readOnlyNames);
          checkedPrompts = new Set(prompts);
          checkedResources = new Set(resources);
          checkedSkills = new Set(skills);
        } else {
          mode = "custom";
          names.forEach((n) => {
            if (tools.includes(n)) checkedTools.add(n);
            else if (prompts.includes(n)) checkedPrompts.add(n);
            else if (resources.includes(n)) checkedResources.add(n);
            else if (skills.includes(n)) checkedSkills.add(n);
          });
        }
      } else {
        mode = "custom";
        serverRules.forEach((r) => {
          if (!r.capabilityNames) return;
          const names = Array.isArray(r.capabilityNames)
            ? r.capabilityNames
            : r.capabilityNames.split(",").map((n) => n.trim());
          names.forEach((n) => {
            if (r.capabilityType === "TOOL") checkedTools.add(n);
            else if (r.capabilityType === "PROMPT") checkedPrompts.add(n);
            else if (r.capabilityType === "RESOURCE") checkedResources.add(n);
            else if (r.capabilityType === "SKILL") checkedSkills.add(n);
            else {
              if (tools.includes(n)) checkedTools.add(n);
              else if (prompts.includes(n)) checkedPrompts.add(n);
              else if (resources.includes(n)) checkedResources.add(n);
              else checkedSkills.add(n);
            }
          });
        });
      }
    }

    map.set(serverName, { mode, checkedTools, checkedPrompts, checkedResources, checkedSkills });
  });

  return map;
}

function buildRules(
  servers: AvailableServer[],
  cardStates: Map<string, ServerCardState>
): CapRule[] {
  const rules: CapRule[] = [];

  servers.forEach((server) => {
    const serverName = server.serverConfigName || server.name || "";
    const state = cardStates.get(serverName);
    if (!state || state.mode === "none") return;

    const tools = (server.tools || []).map(capabilityName);
    const prompts = (server.prompts || []).map(capabilityName);
    const resources = (server.resources || []).map(capabilityName);
    const skills = (server.skills || []).map(capabilityName);

    if (state.mode === "all") {
      rules.push({ serverConfigName: serverName, capabilityType: "ALL", mode: "INCLUDE_ALL", capabilityNames: null });
      return;
    }

    if (state.mode === "readonly") {
      const readOnlyToolNames = tools.filter(isReadOnlyTool);
      if (readOnlyToolNames.length > 0) {
        rules.push({ serverConfigName: serverName, capabilityType: "TOOL", mode: "INCLUDE_ONLY", capabilityNames: readOnlyToolNames });
      }
      if (prompts.length > 0) {
        rules.push({ serverConfigName: serverName, capabilityType: "PROMPT", mode: "INCLUDE_ALL", capabilityNames: null });
      }
      if (resources.length > 0) {
        rules.push({ serverConfigName: serverName, capabilityType: "RESOURCE", mode: "INCLUDE_ALL", capabilityNames: null });
      }
      if (skills.length > 0) {
        rules.push({ serverConfigName: serverName, capabilityType: "SKILL", mode: "INCLUDE_ALL", capabilityNames: null });
      }
      return;
    }

    const byType = [
      { type: "TOOL", checked: state.checkedTools, total: tools },
      { type: "PROMPT", checked: state.checkedPrompts, total: prompts },
      { type: "RESOURCE", checked: state.checkedResources, total: resources },
      { type: "SKILL", checked: state.checkedSkills, total: skills },
    ];
    for (const { type, checked, total } of byType) {
      if (checked.size === 0) continue;
      if (checked.size === total.length) {
        rules.push({ serverConfigName: serverName, capabilityType: type, mode: "INCLUDE_ALL", capabilityNames: null });
      } else {
        rules.push({ serverConfigName: serverName, capabilityType: type, mode: "INCLUDE_ONLY", capabilityNames: Array.from(checked) });
      }
    }
  });

  return rules;
}

interface FormViewProps {
  editingProfile: Profile | null;
  servers: AvailableServer[];
  agents: any[];
  prefilledAgentId?: string;
  onBack: () => void;
  onSaved: () => void;
}

function FormView({ editingProfile, servers, agents, prefilledAgentId, onBack, onSaved }: FormViewProps) {
  const { toast } = useToast();
  const [name, setName] = useState(editingProfile?.name || "");
  const [description, setDescription] = useState(editingProfile?.description || "");
  const [cardStates, setCardStates] = useState<Map<string, ServerCardState>>(
    () => buildServerCardStates(servers, editingProfile)
  );
  const [selectedAgentIds, setSelectedAgentIds] = useState<Set<string>>(
    () => new Set(prefilledAgentId ? [prefilledAgentId] : [])
  );
  const [agentPickerValue, setAgentPickerValue] = useState("");
  const [saving, setSaving] = useState(false);
  const isEdit = !!editingProfile;

  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const availableAgents = agents;
  const unselectedAgents = availableAgents.filter((a) => !selectedAgentIds.has(a.id));

  function addAgent() {
    if (!agentPickerValue) return;
    setSelectedAgentIds((prev) => new Set(prev).add(agentPickerValue));
    setAgentPickerValue("");
  }

  function removeAgent(id: string) {
    setSelectedAgentIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function updateCardState(serverName: string, next: ServerCardState) {
    setCardStates((prev) => {
      const m = new Map(prev);
      m.set(serverName, next);
      return m;
    });
  }

  async function handleSave() {
    if (!name.trim()) {
      toast({ description: "Profile name is required", variant: "destructive" });
      return;
    }
    const rules = buildRules(servers, cardStates);
    const agentIdsToAssign = [...selectedAgentIds];
    const body: any = { name: name.trim(), description: description.trim(), rules };

    setSaving(true);
    try {
      if (isEdit && editingProfile) {
        await GatewayProfilesApi.update(editingProfile.id, body);
        toast({ description: "Profile updated" });
      } else {
        // POST ignores assignToAgents — assign each agent by id after create (mirrors OG saveProfile)
        const created: any = await GatewayProfilesApi.create(body);
        let assignedCount = 0;
        for (const agentId of agentIdsToAssign) {
          try {
            await GatewayProfilesApi.assign(created.id, agentId);
            assignedCount++;
          } catch {
            /* continue assigning the rest */
          }
        }
        toast({
          description:
            assignedCount > 0
              ? `Profile created and assigned to ${assignedCount} agent${assignedCount > 1 ? "s" : ""}`
              : "Profile created",
        });
      }
      onSaved();
    } catch (e: any) {
      toast({ title: "Save failed", description: apiError(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  const prefilledAgent = prefilledAgentId ? agents.find((a) => a.id === prefilledAgentId) : null;

  const configuredServerCount = servers.filter((s) => {
    const st = cardStates.get(s.serverConfigName || s.name || "");
    return st && st.mode !== "none";
  }).length;

  return (
    <>
      <div className="ag-card ag-detail-header">
        <div className="ag-detail-identity">
          <button
            type="button"
            onClick={onBack}
            className="ag-back-button"
            title="Back to Profiles"
            aria-label="Back to Profiles"
          >
            <ArrowLeft />
          </button>
          <span className="ag-detail-avatar" aria-hidden="true">
            <ShieldCheck />
          </span>
          <div className="min-w-0">
            <h1 className="ag-detail-title">{isEdit ? "Edit" : "Create"} Profile</h1>
            <div className="ag-detail-meta">
              <span>Define access rules and architectural boundaries for system agents.</span>
            </div>
          </div>
        </div>
        <div className="ag-toolbar-group">
          <button
            type="button"
            onClick={onBack}
            className="ag-button inline-flex items-center"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="ag-button ag-primary-button inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <RefreshCw className="animate-spin" />
            ) : (
              <Check />
            )}
            {isEdit ? "Update" : "Create"} Profile
          </button>
        </div>
      </div>

      <div className="ag-card ag-content">
        <div className="ag-scroll space-y-6">
          {prefilledAgent && (
            <div className="ag-alert" data-tone="primary">
              <ShieldCheck />
              <span>Creating profile for: <strong>{prefilledAgent.agentName}</strong></span>
            </div>
          )}

          <div className="ag-card p-6 space-y-5">
            <div>
              <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Profile Name <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter profile identity..."
                className="w-full px-4 py-3 rounded-lg border border-border ag-surface text-sm text-foreground placeholder-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly describe the purpose of this profile..."
                rows={3}
                className="w-full px-4 py-3 rounded-lg border border-border ag-surface text-sm text-foreground placeholder-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 resize-none transition-colors"
              />
            </div>
          </div>

          {!isEdit && availableAgents.length > 0 && (
            <div className="ag-card p-6 space-y-4">
              <div>
                <h3 className="text-sm font-bold text-foreground">Assign to Agents</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Authorize specific agents to use these rules.</p>
              </div>
              {selectedAgentIds.size > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Array.from(selectedAgentIds).map((id) => {
                    const ag = agents.find((a: any) => a.id === id);
                    if (!ag) return null;
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-medium"
                      >
                        <Bot className="w-3 h-3" />
                        {ag.agentName}
                        {ag.agentVersion && <span className="text-primary/60">v{ag.agentVersion}</span>}
                        <button
                          type="button"
                          onClick={() => removeAgent(id)}
                          className="hover:text-foreground transition-colors ml-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-2 items-center">
                <select
                  value={agentPickerValue}
                  onChange={(e) => setAgentPickerValue(e.target.value)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-border ag-surface text-sm text-foreground/80 focus:outline-none focus:border-primary/50 transition-colors"
                >
                  <option value="">Select an agent...</option>
                  {unselectedAgents.map((a: any) => (
                    <option key={a.id} value={a.id}>
                      {a.agentName}{a.agentVersion ? ` v${a.agentVersion}` : ""} · {(a.approvalStatus || "UNKNOWN")}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addAgent}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary/10 text-primary border border-primary/30 text-sm hover:bg-primary/20 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-foreground">Server Access Rules</h3>
              <span className="text-xs text-muted-foreground">
                {configuredServerCount} Server{configuredServerCount !== 1 ? "s" : ""} Configured
              </span>
            </div>
            {servers.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center ag-card">
                No servers available. Connect MCP servers first.
              </div>
            ) : (
              <div className="space-y-2">
                {servers.map((server) => {
                  const serverName = server.serverConfigName || server.name || "";
                  const state = cardStates.get(serverName) || {
                    mode: "none" as ServerMode,
                    checkedTools: new Set<string>(),
                    checkedPrompts: new Set<string>(),
                    checkedResources: new Set<string>(),
                    checkedSkills: new Set<string>(),
                  };
                  return (
                    <ServerAccessCard
                      key={serverName}
                      server={server}
                      state={state}
                      onChange={(next) => updateCardState(serverName, next)}
                      isOpen={openAccordion === serverName}
                      onToggle={() => setOpenAccordion(openAccordion === serverName ? null : serverName)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}


interface ProfileDetailProps {
  profileId: string;
  agents: any[];
  onBack: () => void;
  onEdit: () => void;
  onDeleted: () => void;
}

function ProfileDetailView({ profileId, agents, onBack, onEdit, onDeleted }: ProfileDetailProps) {
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [assignSelectValue, setAssignSelectValue] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expandedDetailServer, setExpandedDetailServer] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, prev] = await Promise.all([
        GatewayProfilesApi.get(profileId),
        GatewayProfilesApi.preview(profileId).catch(() => ({})),
      ]);
      setProfile(p as Profile);
      setPreview(prev);
    } catch (e: any) {
      toast({ description: `Failed to load profile: ${apiError(e)}`, variant: "destructive" });
      onBack();
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  function handleDelete() {
    setDeleteConfirmOpen(true);
  }

  async function confirmDelete() {
    if (!profile) return;
    try {
      await GatewayProfilesApi.remove(profileId);
      toast({ description: "Profile deleted" });
      setDeleteConfirmOpen(false);
      onDeleted();
    } catch (e: any) {
      toast({ description: `Delete failed: ${apiError(e)}`, variant: "destructive" });
    }
  }

  async function handleAssign() {
    if (!assignSelectValue) {
      toast({ description: "Select an agent first" });
      return;
    }
    try {
      await GatewayProfilesApi.assign(profileId, assignSelectValue);
      toast({ description: "Agent assigned to profile" });
      setAssignSelectValue("");
      await load();
    } catch (e: any) {
      toast({ description: `Assignment failed: ${apiError(e)}`, variant: "destructive" });
    }
  }

  async function handleUnassign(agentId: string) {
    try {
      await GatewayProfilesApi.unassign(profileId, agentId);
      toast({ description: "Agent removed from profile" });
      await load();
    } catch (e: any) {
      toast({ description: `Unassignment failed: ${apiError(e)}`, variant: "destructive" });
    }
  }

  if (loading || !profile) {
    return (
      <div className="flex items-center gap-2 py-16 justify-center text-muted-foreground text-sm">
        <RefreshCw className="w-5 h-5 animate-spin" /> Loading profile…
      </div>
    );
  }

  const rules = profile.rules || [];
  const byServer: Record<string, CapRule[]> = {};
  rules.forEach((r) => {
    if (!byServer[r.serverConfigName]) byServer[r.serverConfigName] = [];
    byServer[r.serverConfigName].push(r);
  });
  const serverBreakdown = (preview as any)?.serverBreakdown || {};
  let ruleCounter = 1;

  const assignedAgents = profile.assignedAgents || [];
  const assignedIds = new Set(assignedAgents.map((a) => a.agentId));
  const unassignedAgents = agents.filter((a) => !assignedIds.has(a.id));

  return (
    <>
      <div className="ag-card ag-detail-header">
        <div className="ag-detail-identity">
          <button
            type="button"
            onClick={onBack}
            className="ag-back-button"
            title="Back to Profiles"
            aria-label="Back to Profiles"
          >
            <ArrowLeft />
          </button>
          <span className="ag-detail-avatar" data-tone={profile.isTemplate ? "amber" : undefined} aria-hidden="true">
            {profile.isTemplate ? <BookMarked aria-label="Template" /> : <ShieldCheck />}
          </span>
          <div className="min-w-0">
            <h1 className="ag-detail-title">
              <span className="truncate">{profile.name}</span>
            </h1>
            <div className="ag-detail-meta">
              <span className="truncate">{profile.description || "No description provided."}</span>
            </div>
          </div>
        </div>
        <div className="ag-toolbar-group">
          <span className="ag-meta">
            <Database aria-hidden="true" />
            Created <span className="ag-mono text-foreground/80">{formatTimestamp(profile.createdAt)}</span>
          </span>
          <span className="ag-meta">
            <RefreshCw aria-hidden="true" />
            Updated <span className="ag-mono text-foreground/80">{formatTimestamp(profile.updatedAt)}</span>
          </span>
          <button
            type="button"
            className="ag-button inline-flex items-center"
            onClick={onEdit}
          >
            <Pencil />
            Edit
          </button>
          <button
            type="button"
            className="ag-button ag-danger-button inline-flex items-center"
            onClick={handleDelete}
          >
            <Trash2 />
            Delete
          </button>
        </div>
      </div>

      <div className="ag-card ag-content">
        <div className="ag-scroll">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-foreground">Capability Rules</h3>
              <span className="text-xs text-muted-foreground">
                {Object.keys(byServer).length} Active Integration{Object.keys(byServer).length !== 1 ? "s" : ""}
              </span>
            </div>
            {rules.length === 0 ? (
              <div className="ag-card p-6 text-center text-sm text-muted-foreground">
                No rules defined. This profile grants no access.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {Object.entries(byServer).map(([srvName, srvRules], srvIdx) => {
                  const bd = serverBreakdown[srvName] || {};
                  const isExpanded = expandedDetailServer === srvName;
                  // SKILL rules target another agent (A2A) — agents don't have tool/prompt/resource counts.
                  const isSkillRule = srvRules.some((r) => r.capabilityType === "SKILL");
                  return (
                    <div key={srvName} className="ag-card overflow-hidden portal-card-hover">
                      <button
                        type="button"
                        onClick={() => setExpandedDetailServer(isExpanded ? null : srvName)}
                        className="w-full flex items-center gap-3 px-5 py-3.5"
                      >
                        <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                          <Server className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <span className="font-bold text-foreground text-sm">{srvName}</span>
                          <p className="text-[11px] text-muted-foreground">
                            {srvRules.length > 1 ? "Multiple capability rules" : `${srvRules[0].capabilityType} Automation Rule`}
                          </p>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded ag-surface border border-border text-muted-foreground font-mono uppercase tracking-wider shrink-0">
                          {srvRules.length} Rule{srvRules.length !== 1 ? "s" : ""}
                        </span>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="border-t border-border">
                          {/* Tool/Prompt/Resource counts only apply to MCP servers — agents (SKILL rules) have none */}
                          {!isSkillRule && (
                            <div className="px-5 py-3">
                              <div className="grid grid-cols-3 gap-2">
                                <div className="rounded-lg ag-surface border border-border py-1.5 px-2 text-center">
                                  <p className="text-sm font-bold text-foreground">{bd.allowedTools ?? "?"}/{bd.totalTools ?? "?"}</p>
                                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Tools</p>
                                </div>
                                <div className="rounded-lg ag-surface border border-border py-1.5 px-2 text-center">
                                  <p className="text-sm font-bold text-foreground">{bd.allowedPrompts ?? "?"}/{bd.totalPrompts ?? "?"}</p>
                                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Prompts</p>
                                </div>
                                <div className="rounded-lg ag-surface border border-border py-1.5 px-2 text-center">
                                  <p className="text-sm font-bold text-foreground">{bd.allowedResources ?? "?"}/{bd.totalResources ?? "?"}</p>
                                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Resources</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {srvRules.map((r, i) => {
                            const ruleNum = ruleCounter++;
                            const names = r.capabilityNames
                              ? (Array.isArray(r.capabilityNames)
                                  ? r.capabilityNames
                                  : r.capabilityNames.split(",").map((n: string) => n.trim())).filter(Boolean)
                              : [];
                            return (
                              <div key={i} className="px-5 py-3 border-b border-border last:border-b-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    Active {r.capabilityType === "TOOL" ? "Tools" : r.capabilityType === "PROMPT" ? "Prompts" : r.capabilityType === "SKILL" ? "Skills" : "Resources"}
                                  </span>
                                  <span className={cn("inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] font-semibold border", capabilityTypeColor(r.capabilityType))}>
                                    {capTypeIcon(r.capabilityType)} {r.capabilityType}
                                  </span>
                                  <span className={cn(
                                    "px-1.5 py-px rounded text-[10px] font-semibold border",
                                    r.mode === "EXCLUDE"
                                      ? "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
                                      : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                                  )}>
                                    {ruleModeLabel(r.mode)}
                                  </span>
                                  <span className="ml-auto text-[10px] px-1.5 py-px rounded ag-surface border border-border text-muted-foreground font-mono">
                                    Rule #{String(ruleNum).padStart(3, "0")}
                                  </span>
                                </div>
                                {names.length > 0 ? (
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {names.map((n: string) => (
                                      <div
                                        key={n}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ag-surface border border-border whitespace-nowrap"
                                      >
                                        <Wrench className="w-3.5 h-3.5 text-primary shrink-0" />
                                        <span className="text-[13px] font-mono text-foreground">{n}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground italic">All capabilities included</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

            {/* Right: assigned agents + add new agent, stacked */}
            <div className="space-y-4">
            <div className="ag-card p-5">
              <div className="flex items-center gap-2 mb-4">
                <Bot className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-bold text-foreground">Assigned Agents</h3>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full ag-surface text-muted-foreground font-medium">
                  {assignedAgents.length} Total
                </span>
              </div>
              {assignedAgents.length === 0 ? (
                <p className="text-xs text-muted-foreground">No agents assigned yet.</p>
              ) : (
                <div className="space-y-2">
                  {assignedAgents.map((a: any) => (
                    <div
                      key={a.agentId}
                      className="flex items-center justify-between rounded-lg border border-border ag-surface px-4 py-3 portal-card-hover"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-foreground">{a.agentName}</span>
                          {a.agentVersion && (
                            <p className="text-[11px] text-muted-foreground">{a.agentVersion}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnassign(a.agentId)}
                        className="flex items-center gap-1 text-[11px] text-red-600 dark:text-red-400 hover:text-red-300 transition-colors"
                      >
                        <X className="w-3 h-3" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="ag-card p-5">
              <div className="flex items-center gap-2 mb-4">
                <Plus className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-bold text-foreground">Add New Agent</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                    Target Agent
                  </label>
                  <select
                    value={assignSelectValue}
                    onChange={(e) => setAssignSelectValue(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-border ag-surface text-sm text-foreground/80 focus:outline-none focus:border-primary/50 transition-colors"
                  >
                    <option value="">Select agent...</option>
                    {unassignedAgents.map((a: any) => (
                      <option key={a.id} value={a.id}>
                        {a.agentName}{a.agentVersion ? ` v${a.agentVersion}` : ""} · {a.approvalStatus || "UNKNOWN"}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Agents assigned here will be governed by the capability rules defined in this profile.
                </p>
                <button
                  onClick={handleAssign}
                  disabled={!assignSelectValue}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Link2 className="w-4 h-4" />
                  Assign Agent
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="management-dialog ag-dialog ag-confirm">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" data-tone="danger" aria-hidden="true">
                <Trash2 />
              </span>
              <div>
                <DialogTitle>Delete Profile</DialogTitle>
                <DialogDescription>
                  Delete profile <strong>"{profile?.name}"</strong>? Agents using this profile will lose access.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="management-dialog-footer">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} className="ag-danger-button">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


type ChatMessageExt = ChatMessage & { pendingProfile?: any };

interface ChatPanelWrapperProps {
  agents: any[];
  onProfileCreated: () => void;
  onClose: () => void;
  messages: ChatMessageExt[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageExt[]>>;
  pendingProfile: any;
  setPendingProfile: React.Dispatch<React.SetStateAction<any>>;
}

function ChatPanelWrapper({ agents, onProfileCreated, onClose, messages, setMessages, pendingProfile, setPendingProfile }: ChatPanelWrapperProps) {
  const { toast } = useToast();
  const [chatLoading, setChatLoading] = useState(false);

  const suggestions = [
    "Grant read-only access to all tools for a specific agent",
    "Create a restrictive profile that only allows get_me tool",
    "What servers and agents are available?",
    "Full access profile for all connected servers",
  ];

  async function handleSend(text: string) {
    const userMsg: ChatMessage = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setChatLoading(true);

    try {
      const result: any = await GatewayProfilesApi.chat({
        prompt: text,
        conversationHistory: messages.map((m) => ({ role: m.role, content: m.content })),
      });

      if (result.error) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Error: ${result.error}` },
        ]);
        return;
      }

      if (result.success && result.profile) {
        setPendingProfile(result.profile);
        const profileData = result.profile;
        const content = `__PROFILE_CARD__${JSON.stringify(profileData)}__END_PROFILE_CARD__`;
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content, pendingProfile: profileData },
        ]);
      } else if (result.message) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: result.message },
        ]);
      }
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Failed: ${apiError(e)}` },
      ]);
    } finally {
      setChatLoading(false);
    }
  }

  async function applyPendingProfile(profileData: any) {
    const body = {
      name: profileData.name,
      description: profileData.description,
      rules: (profileData.rules || []).map((r: any) => ({
        serverConfigName: r.serverConfigName,
        capabilityType: r.capabilityType || "ALL",
        mode: r.mode,
        capabilityNames: r.capabilityNames || null,
      })),
    };
    try {
      const created: any = await GatewayProfilesApi.create(body);
      const assignNames: string[] = profileData.assignToAgents || [];
      let assignedCount = 0;

      let agentsList = agents;
      if (agentsList.length === 0) {
        try {
          agentsList = await GatewayAgentsApi.list();
        } catch { agentsList = []; }
      }

      if (created?.id && assignNames.length > 0) {
        for (const name of assignNames) {
          const lc = name.toLowerCase();
          const match = agentsList.find(
            (a: any) =>
              (a.agentName || "").toLowerCase() === lc ||
              (a.agentName || "").toLowerCase().includes(lc)
          );
          if (match) {
            try {
              await GatewayProfilesApi.assign(created.id, match.id);
              assignedCount++;
            } catch {}
          }
        }
      }
      toast({
        description:
          assignedCount > 0
            ? `Profile created and assigned to ${assignedCount} agent${assignedCount > 1 ? "s" : ""}`
            : "Profile created from AI assistant",
      });
      setPendingProfile(null);
      onProfileCreated();
    } catch (e: any) {
      toast({ description: `Failed to create profile: ${apiError(e)}`, variant: "destructive" });
    }
  }

  const augmentedMessages: ChatMessage[] = messages.map(({ role, content, timestamp }) => ({
    role,
    content,
    timestamp,
  }));

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl rounded-[18px] border border-border/60 bg-card shadow-[0_24px_80px_rgb(15_23_42/0.18)] flex flex-col h-[75vh] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">
              AI Profile Assistant
            </span>
          </div>
          <button
            className="text-muted-foreground hover:text-foreground transition-colors"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 flex flex-col min-h-0">
          <GatewayChatPanel
            messages={augmentedMessages}
            onSend={handleSend}
            loading={chatLoading}
            suggestions={suggestions}
            placeholder="Describe the profile you want…"
            welcomeMessage="Describe the access profile you need. I can create profiles based on your description, suggest configurations, and automatically assign agents."
            className="flex-1"
            customRenderer={(content) => {
              const match = content.match(/^__PROFILE_CARD__([\s\S]+)__END_PROFILE_CARD__$/);
              if (!match) return null;
              try {
                const p = JSON.parse(match[1]);
                const rules: any[] = p.rules || [];
                return (
                  <div className="w-full">
                    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-primary" />
                        <span className="text-sm font-bold text-foreground">{p.name || "Unnamed Profile"}</span>
                      </div>
                      {p.description && (
                        <p className="text-xs text-muted-foreground leading-relaxed">{p.description}</p>
                      )}

                      {rules.length > 0 && (
                        <div className="space-y-2 pt-1">
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                            Access Rules ({rules.length})
                          </p>
                          {rules.map((r: any, i: number) => {
                            const names = Array.isArray(r.capabilityNames) ? r.capabilityNames : r.capabilityNames ? [r.capabilityNames] : [];
                            return (
                              <div key={i} className="rounded-lg border border-border bg-card p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <Server className="w-3.5 h-3.5 text-primary" />
                                  <span className="text-xs font-semibold text-foreground">{r.serverConfigName}</span>
                                  <span className="text-[10px] px-1.5 py-px rounded ag-surface border border-border text-muted-foreground font-medium">{r.capabilityType}</span>
                                  <span className="text-[10px] text-primary font-medium ml-auto">{ruleModeLabel(r.mode)}</span>
                                </div>
                                {names.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {names.slice(0, 8).map((n: string, j: number) => (
                                      <span key={j} className="inline-block px-2 py-0.5 rounded-full border border-foreground/15 ag-surface text-[10px] font-mono text-foreground/70">{n}</span>
                                    ))}
                                    {names.length > 8 && (
                                      <span className="text-[10px] text-primary font-medium px-1.5 py-0.5">+{names.length - 8} more</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {p.assignToAgents?.length > 0 && (
                        <div className="flex items-center gap-2 pt-1">
                          <Bot className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-[11px] text-muted-foreground">Auto-assign to:</span>
                          {p.assignToAgents.map((a: string, i: number) => (
                            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">{a}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground/60 mt-2 italic">Click "Create This Profile" below to apply, or keep chatting to refine.</p>
                  </div>
                );
              } catch { return null; }
            }}
          />
          {pendingProfile && (
            <div className="border-t border-border px-4 py-3 flex items-center justify-between bg-card">
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground/80">
                  "{pendingProfile.name}"
                </span>{" "}
                — {(pendingProfile.rules || []).length} rule{(pendingProfile.rules || []).length !== 1 ? "s" : ""}
              </div>
              <div className="flex gap-2">
                <button
                  className="text-xs px-2 py-1 rounded text-muted-foreground hover:text-foreground/80 transition-colors"
                  onClick={() => setPendingProfile(null)}
                >
                  Discard
                </button>
                <button
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-primary text-foreground font-semibold hover:bg-primary transition-colors"
                  onClick={() => applyPendingProfile(pendingProfile)}
                >
                  <Check className="w-3 h-3" />
                  Create This Profile
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function GatewayProfilesPage() {
  const { toast } = useToast();

  const [view, setView] = useState<PageView>("list");
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [servers, setServers] = useState<AvailableServer[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [prefilledAgentId, setPrefilledAgentId] = useState<string | undefined>();
  const [listDeleteTarget, setListDeleteTarget] = useState<{id: string; name: string} | null>(null);

  const [chatMessages, setChatMessages] = useState<ChatMessageExt[]>([]);
  const [chatPendingProfile, setChatPendingProfile] = useState<any>(null);


const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [profileList, serverList, agentList] = await Promise.all([
        GatewayProfilesApi.list().catch(() => []),
        GatewayProfilesApi.availableServers().catch(() => []),
        GatewayAgentsApi.list().catch(() => []),
      ]);
      setProfiles((profileList as any) || []);
      setServers((serverList as any) || []);
      setAgents((agentList as any) || []);
    } catch (e: any) {
      toast({ description: `Failed to load profiles: ${apiError(e)}`, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);


function goToCreate(agentId?: string) {
    setEditingProfile(null);
    setPrefilledAgentId(agentId);
    setView("form");
  }

  async function goToEdit(profileId: string) {
    try {
      const p: any = await GatewayProfilesApi.get(profileId);
      setEditingProfile(p as Profile);
      setPrefilledAgentId(undefined);
      setView("form");
    } catch (e: any) {
      toast({ description: `Failed to load profile: ${apiError(e)}`, variant: "destructive" });
    }
  }

  function goToDetail(profileId: string) {
    setSelectedProfileId(profileId);
    setView("detail");
  }

  function backToList() {
    setView("list");
    setEditingProfile(null);
    setSelectedProfileId(null);
    setPrefilledAgentId(undefined);
    loadAll();
  }

  function handleDeleteFromList(e: React.MouseEvent, profileId: string, profileName: string) {
    e.stopPropagation();
    setListDeleteTarget({ id: profileId, name: profileName });
  }

  async function confirmListDelete() {
    if (!listDeleteTarget) return;
    try {
      await GatewayProfilesApi.remove(listDeleteTarget.id);
      toast({ description: "Profile deleted" });
      setListDeleteTarget(null);
      loadAll();
    } catch (err: any) {
      toast({ description: `Delete failed: ${err.message}`, variant: "destructive" });
    }
  }


if (view === "detail" && selectedProfileId) {
    return (
      <div className="ag-page">
        <ProfileDetailView
          profileId={selectedProfileId}
          agents={agents}
          onBack={backToList}
          onEdit={() => goToEdit(selectedProfileId)}
          onDeleted={backToList}
        />
      </div>
    );
  }


if (view === "form") {
    return (
      <div className="ag-page">
        <FormView
          editingProfile={editingProfile}
          servers={servers}
          agents={agents}
          prefilledAgentId={prefilledAgentId}
          onBack={backToList}
          onSaved={backToList}
        />
      </div>
    );
  }


return (
    <div className="ag-page">
      {showChat && (
        <ChatPanelWrapper
          agents={agents}
          onProfileCreated={() => { setShowChat(false); setChatMessages([]); setChatPendingProfile(null); loadAll(); }}
          onClose={() => setShowChat(false)}
          messages={chatMessages}
          setMessages={setChatMessages}
          pendingProfile={chatPendingProfile}
          setPendingProfile={setChatPendingProfile}
        />
      )}

      <div className="ag-card ag-toolbar">
        <span className="ag-summary">
          <ShieldCheck aria-hidden="true" />
          <span>
            <strong>{profiles.length}</strong> profile{profiles.length !== 1 ? "s" : ""} · Default deny: agents with no profiles see nothing
          </span>
        </span>
        {profiles.length > 0 && (
          <div className="ag-toolbar-group">
            <button
              type="button"
              className="ag-button inline-flex items-center group"
              onClick={() => setShowChat(true)}
            >
              <Wand2 className="text-primary group-hover:rotate-12 transition-transform duration-300" />
              AI Assistant
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            </button>
            <button
              type="button"
              className="ag-button ag-primary-button inline-flex items-center"
              onClick={() => goToCreate()}
            >
              <Plus />
              Create Profile
            </button>
          </div>
        )}
      </div>

      {profiles.length > 0 && (
        <div className="ag-stat-grid">
          <GatewayStatCard
            icon={<Lock />}
            label="Total Profiles"
            value={profiles.length}
            colorClass="text-primary"
          />
          <GatewayStatCard
            icon={<Server />}
            label="Servers Covered"
            value={new Set(profiles.flatMap((p) => (p.rules || []).map((r) => r.serverConfigName))).size}
            colorClass="text-emerald-600 dark:text-emerald-400"
          />
          <GatewayStatCard
            icon={<Users />}
            label="Total Assignments"
            value={profiles.reduce((s, p) => s + (p.assignedAgentCount || 0), 0)}
            colorClass="text-violet-600 dark:text-violet-400"
          />
        </div>
      )}

      <div className="ag-card ag-content">
        <div className="ag-scroll">
          {loading ? (
            <div className="ag-loading">
              <span className="ag-spinner animate-spin" aria-hidden="true" />
              <span>Loading profiles…</span>
            </div>
          ) : profiles.length === 0 ? (
            <div className="ag-empty h-full">
              <span className="ag-empty-icon" aria-hidden="true">
                <Lock />
              </span>
              <p className="ag-empty-title">No capability profiles yet</p>
              <p className="max-w-sm">
                Agents cannot see any capabilities until you create and assign profiles.
              </p>
              <button
                type="button"
                className="ag-button ag-primary-button inline-flex items-center mt-2"
                onClick={() => goToCreate()}
              >
                <Plus />
                Create your first profile
              </button>
              <button
                type="button"
                className="ag-button inline-flex items-center"
                onClick={() => setShowChat(true)}
              >
                <Wand2 className="text-primary" />
                Or let AI build one for you
              </button>
            </div>
          ) : (
            <div className="ag-server-list">
              {profiles.map((p) => (
                <ProfileCard
                  key={p.id}
                  profile={p}
                  onClick={() => goToDetail(p.id)}
                  onEdit={(e) => { e.stopPropagation(); goToEdit(p.id); }}
                  onDelete={(e) => handleDeleteFromList(e, p.id, p.name)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!listDeleteTarget} onOpenChange={(open) => { if (!open) setListDeleteTarget(null); }}>
        <DialogContent className="management-dialog ag-dialog ag-confirm">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" data-tone="danger" aria-hidden="true">
                <Trash2 />
              </span>
              <div>
                <DialogTitle>Delete Profile</DialogTitle>
                <DialogDescription>
                  Delete profile <strong>"{listDeleteTarget?.name}"</strong>? Agents using this profile will lose access.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="management-dialog-footer">
            <Button variant="outline" onClick={() => setListDeleteTarget(null)}>Cancel</Button>
            <Button onClick={confirmListDelete} className="ag-danger-button">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
